import { AntdProTable, SearchInput } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getFacturasByIdSimple } from "api/Factura.service"
import {
  QueryParamsProps,
  getPagosCobrosById,
  getPagosCobrosDisponiblesFacturaId,
  putPagosCobrosAddFactura,
} from "api/FlujoTesoreria.service"
import {
  TIPO_FLUJO_TESORERIA,
  TipoFlujoTesoreriaType,
} from "api/Types/TipoFlujoTesoreria"
import {
  DATE_DD_MM_YYYY,
  DATE_YYYY_MM_DD,
} from "common/components/FilterMenu/constants"
import { formatCurrency, formatTitle } from "common/utils/currency"
import notification from "common/utils/notification"
import { CURRENT_DATE, START_YEAR_DATE, formatDate } from "common/utils/utils"
import CalendarioFilter from "features/Contabilidad/pages/LibroDiario/components/CalendarioFilter"
import { uploadValueDocumentoModal } from "features/Documentos/redux/documentosSlice"
import { AnimateSharedLayout, motion } from "framer-motion"
import { antdProTablePaginationHandler } from "handlers/antdProTablePaginationHandler"
import { antdProTableRequestHandler } from "handlers/antdProTableRequestHandler"
import { useDispatch } from "hooks/useDispatch"
import { useFilterMenu } from "hooks/useFilterMenu"
import { AntdProTableActionsProps } from "interfaces/AntdProTableActions"
import {
  Dispatch,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import {
  COLUMNAS_ADD_PAGO_COBRO_MODAL,
  getSumOfImportes,
} from "../../constants"
import AddPagoCobroAdapter from "./addPagoCobroModal.adapter"

export interface ItemFlujoDeTesoreriaDisponibleByFacturaQueryDto {
  id: string
  descripcion: string | undefined
  fecha: string | undefined
  importe: number | undefined
  identificador: number | string
}

interface FechasFilterState {
  0: Date
  1: Date
}
interface AddPagoCobroModalProps {
  mode: TipoFlujoTesoreriaType | undefined
  handleOpenCreateNewPago: () => void
  onHide: () => void
  facturaId: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  document: $TSMigrationFixMe
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setNewPagoCobro: $TSMigrationFixMe
  newPagoCobro: string[]
  refetchCobroPago: () => void
}
interface SortingCriteria {
  sort: string | undefined | null
  property: string | undefined
}

const AddPagoCobroModal: FC<AddPagoCobroModalProps> = ({
  mode,
  handleOpenCreateNewPago,
  onHide,
  facturaId,
  document,
  newPagoCobro,
  setNewPagoCobro,
  refetchCobroPago,
}: AddPagoCobroModalProps) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()
  const [show, setShow] = useState(false)
  const [localSearch, setLocalSearch] = useState<string>("")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedOptions, setSelectedOptions] = useState<any[]>([])
  const [fechasFilter, setFechasFilter] = useState<FechasFilterState | null>({
    0: new Date(`${START_YEAR_DATE}`),
    1: new Date(`${CURRENT_DATE}`),
  })
  const paginationSkipCount = useRef(null)
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 10,
    skipCount: 0,
  })
  const refTable = useRef<AntdProTableActionsProps>()
  const handleResetPagination = () => {
    refTable && refTable?.current?.reset()
  }

  const fetchItems = useCallback(() => {
    return async (queryParams: QueryParamsProps) => {
      try {
        if (!facturaId) return
        const result = await getPagosCobrosDisponiblesFacturaId(
          facturaId,
          queryParams
        )
        return result
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching items:", error)
        throw error
      }
    }
  }, [facturaId])

  const { items, isLoading, totalCount, setServiceQuery } = useFilterMenu({
    serviceCall: fetchItems,
    serviceAdapter: AddPagoCobroAdapter,
    initialQuery: {
      FechaLowerLimit: formatDate(fechasFilter?.[0], DATE_YYYY_MM_DD),
      FechaUpperLimit: formatDate(fechasFilter?.[1], DATE_YYYY_MM_DD),
      MaxResultCount: pagination.itemsPerPage || 10,
    },
  })

  const handleCalendarioFilter = useCallback(
    (value: Array<Date | null>) => {
      setServiceQuery((preValue: QueryParamsProps) => {
        const updatedValue = {
          ...preValue,
        }
        if (value[0] !== null) {
          updatedValue.FechaLowerLimit = formatDate(value[0], DATE_YYYY_MM_DD)
          updatedValue.SkipCount = 0
        } else {
          delete updatedValue.FechaLowerLimit
        }
        if (value[1] !== null) {
          updatedValue.FechaUpperLimit = formatDate(value[1], DATE_YYYY_MM_DD)
          updatedValue.SkipCount = 0
        } else {
          delete updatedValue.FechaUpperLimit
        }

        return updatedValue
      })
      handleResetPagination()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fechasFilter, setFechasFilter]
  )

  const resetCalendarioFilter = () => setFechasFilter(null)

  const onSearch = (value: string): void => {
    setLocalSearch(value)
    setPagination({
      itemsPerPage: 10,
      skipCount: 0,
    })
    setServiceQuery((preValue: QueryParamsProps) => {
      const updatedValue = {
        ...preValue,
        Keyword: value.trim() !== "" ? value.trim() : undefined,
      }

      return updatedValue
    })
    handleResetPagination()
  }

  const onRenderCalendario = useMemo(
    () => (
      <div className='!tw-cursor-pointer'>
        <CalendarioFilter
          fechasFilter={fechasFilter}
          setFechasFilter={setFechasFilter}
          handleCalendarioFilter={handleCalendarioFilter}
          selectedOptions={[]}
          resetPagination={resetCalendarioFilter}
          deleteQuery={["FechaInicioAsiento", "FechaFinAsiento"]}
          setQuery={undefined}
          query={undefined}
          fetch={undefined}
          setSelectedOptions={undefined}
        />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fechasFilter, setFechasFilter]
  )

  const handleRowSelection = {
    selectedRowKeys: [
      ...selectedOptions.map(
        (option: ItemFlujoDeTesoreriaDisponibleByFacturaQueryDto) => {
          return option.id
        }
      ),
      ...newPagoCobro.filter((newItem: string) => {
        return !selectedOptions.some(
          (option: ItemFlujoDeTesoreriaDisponibleByFacturaQueryDto) =>
            option.id === newItem
        )
      }),
    ],
    preserveSelectedRowKeys: true,
    getCheckboxProps: (
      checkbox: ItemFlujoDeTesoreriaDisponibleByFacturaQueryDto
    ) => {
      const isImporteValid =
        document?.importePendiente - getSumOfImportes(selectedOptions) <= 0
      const isSelected = selectedOptions.some(
        (option) => option.id === checkbox.id
      )
      return {
        disabled: isImporteValid && !isSelected,
      }
    },
    onChange: (selectedRowKeys: string[]) => {
      const updatedNewPagoCobro = newPagoCobro.filter((item) =>
        selectedRowKeys.includes(item)
      )

      setNewPagoCobro(updatedNewPagoCobro)

      const updatedSelectedOptionsFiltered = selectedOptions.filter((option) =>
        updatedNewPagoCobro.includes(option.id)
      )
      const newItemsToAdd = selectedRowKeys
        .filter(
          (id) =>
            !updatedSelectedOptionsFiltered.some((option) => option.id === id)
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((id) => items.find((item: $TSMigrationFixMe) => item.id === id))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter(Boolean) as any[]

      const finalUpdatedSelectedOptions = [
        ...updatedSelectedOptionsFiltered,
        ...newItemsToAdd,
      ]

      setSelectedOptions(
        selectedRowKeys.length === 0 ? [] : finalUpdatedSelectedOptions
      )
    },
  }

  const handleAddPagosCobros = async () => {
    if (!facturaId) return
    const selectedIdOptions = selectedOptions.map(
      (option: ItemFlujoDeTesoreriaDisponibleByFacturaQueryDto) => option.id
    )

    const resp = await putPagosCobrosAddFactura({
      facturaId,
      flujoTesoreriaIds: selectedIdOptions,
    })
    if (resp.status === 200) {
      notification.success({
        body: `${
          mode === TIPO_FLUJO_TESORERIA.COBRO
            ? t("notification.cobroCorrect")
            : t("notification.pagoCorrect")
        }`,
      })
      refetchCobroPago()
      const data = await getFacturasByIdSimple(facturaId)
      // @ts-expect-error TS(2345): Argument of type...
      dispatch(uploadValueDocumentoModal(data))
      onHide()
      setSelectedOptions([])
    } else {
      notification.errorServidor({ body: resp.data })
    }
  }

  useEffect(() => {
    setServiceQuery((preValue: QueryParamsProps) => {
      const updatedValue = {
        ...preValue,
        MaxResultCount: pagination.itemsPerPage || preValue?.MaxResultCount,
        SkipCount: pagination.skipCount || preValue?.SkipCount,
        SortingCriteria:
          sortingCriteria.property && sortingCriteria.sort
            ? `${sortingCriteria.property} ${sortingCriteria.sort}`
            : undefined,
      }

      if (!pagination.itemsPerPage && !preValue?.MaxResultCount) {
        delete updatedValue.MaxResultCount
      }
      if (!pagination.skipCount) {
        delete updatedValue.SkipCount
      }
      if (!sortingCriteria.property || !sortingCriteria.sort) {
        delete updatedValue.SortingCriteria
      }
      return updatedValue
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sortingCriteria, newPagoCobro])

  const handleSortingCriterial = ({
    sort,
    property,
  }: {
    sort: string
    property: string
  }) => {
    setSortingCriteria({
      sort: sort ?? null,
      property: property
        ? property.charAt(0).toUpperCase() + property.slice(1)
        : undefined,
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        newPagoCobro.forEach(async (pagoCobro: string) => {
          const data = await getPagosCobrosById(pagoCobro)

          if (!selectedOptions.some((option) => option.id === data.id)) {
            setSelectedOptions((prevOptions) => [
              ...prevOptions,
              {
                id: data.id,
                fecha: formatDate(data.fecha, DATE_DD_MM_YYYY),
                descripcion: data.descripcion,
                importe: data.importe,
                identificador: "-",
              },
            ])
          }
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error)
      }
    }

    newPagoCobro[0] && fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPagoCobro])

  return (
    <div className='tw-flex tw-flex-col !tw-p-0'>
      <div className='tw-flex tw-flex-row tw-items-center tw-justify-between tw-h-16 tw-mb-4'>
        <p className='tw-m-0 tw-font-medium tw-text-lg'>
          {mode === TIPO_FLUJO_TESORERIA.COBRO
            ? formatTitle(t("crear.factura.addPagoCobro.titleCobro"))
            : formatTitle(t("crear.factura.addPagoCobro.titlePago"))}
        </p>
        <div className='tw-flex tw-flex-row tw-gap-3'>
          <SearchInput
            placeholder={`${t("filtros.contactos.placeholderSearch")} ${
              mode === TIPO_FLUJO_TESORERIA.COBRO
                ? t("crear.factura.addPagoCobro.titleCobro")
                : t("crear.factura.addPagoCobro.titlePago")
            }`}
            onChange={(ev: string) => {
              onSearch(ev)
            }}
            inputValue={localSearch}
            className='tw-h-10 tw-w-72'
          />

          {onRenderCalendario}
          <div
            onClick={() => {
              handleOpenCreateNewPago()
              // onHide()
            }}
            className='tw-w-[120px] tw-h-[40px] tw-flex tw-justify-center tw-items-center tw-text-simple-10 tw-bg-secondary tw-rounded tw-cursor-pointer'
          >
            {t("pagoCobro.newButton")}
          </div>
        </div>
      </div>

      <AnimateSharedLayout>
        <motion.div layout>
          <div
            className={`tw-h-[352px] tw-overflow-auto ${
              !show ? "" : "tw-hidden"
            }`}
          >
            <AntdProTable
              actionRef={refTable}
              rowKey='id'
              defaultSelectionModeAll={false}
              isLoading={isLoading}
              columns={COLUMNAS_ADD_PAGO_COBRO_MODAL(t)}
              items={items}
              rowSelection={handleRowSelection}
              request={antdProTableRequestHandler({
                pagination,
                setPagination,
                setSortingCriteria: handleSortingCriterial,
                items,
                totalCount,
              })}
              pagination={antdProTablePaginationHandler({
                totalCount,
                t,
                paginationSkipCount,
              })}
            />
          </div>
        </motion.div>

        <motion.div
          layout
          onClick={() => setShow(!show)}
          className='tw-flex tw-flex-col tw-justify-between tw-p-3.5 tw-items-center'
          title='Ver los seleccionados '
        >
          <div className='line' />
          <span className='tw-mt-[9px] !tw-text-base tw-text-secondary'>
            {selectedOptions.length}
            {t("crear.facturaRec.modal.selectedLabel")}{" "}
            <FontAwesomeIcon icon={["fal", show ? "angle-down" : "angle-up"]} />
          </span>
          <span
            className={`tw-h-[352px] tw-w-full tw-overflow-auto ${
              show ? "" : "tw-hidden"
            }`}
          >
            <AntdProTable
              rowKey='id'
              defaultSelectionModeAll={false}
              isLoading={isLoading}
              columns={COLUMNAS_ADD_PAGO_COBRO_MODAL(t)}
              items={selectedOptions}
              rowSelection={handleRowSelection}
              pagination={false}
            />
          </span>
        </motion.div>
      </AnimateSharedLayout>

      <div className='tw-flex tw-flex-row tw-justify-between tw-items-center tw-h-[72px] tw-mt-3'>
        <span className='tw-font-medium tw-text-lg'>
          {t("crear.factura.addPagoCobro.selectedImport")}{" "}
          {formatCurrency(getSumOfImportes(selectedOptions))}
        </span>
        <div className='tw-flex tw-flex-row'>
          <div
            onClick={onHide}
            className='tw-mr-4 tw-w-[140px] tw-h-12 tw-flex tw-justify-center tw-items-center tw-text-base tw-text-text tw-bg-transparent tw-rounded tw-cursor-pointer'
          >
            {t("crear.factura.addPagoCobro.cancel")}
          </div>
          <div
            onClick={() =>
              selectedOptions.length !== 0 && handleAddPagosCobros()
            }
            className={`tw-min-w-[140px] tw-h-12 tw-flex tw-justify-center tw-items-center tw-text-base tw-text-simple-10 ${
              selectedOptions.length !== 0
                ? "tw-bg-primary tw-cursor-pointer"
                : "tw-bg-primary-25"
            } tw-rounded`}
          >
            {t("crear.factura.addPagoCobro.addLabel")}{" "}
            {mode === TIPO_FLUJO_TESORERIA.COBRO
              ? t("crear.factura.addPagoCobro.titleCobro")
              : t("crear.factura.addPagoCobro.titlePago")}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddPagoCobroModal
